import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Radio, CheckCircle2, Loader2, XCircle } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");

  const poll = useCallback(async (sessionId, attempt) => {
    if (attempt >= MAX_POLLS) { setStatus("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      setTimeout(() => poll(sessionId, attempt + 1), POLL_INTERVAL);
    } catch (e) {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    poll(sessionId, 0);
  }, [poll]);

  const primaryBtn = "w-full rounded-full bg-[#243447] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2E4056]";
  const ghostBtn = "w-full rounded-full border border-[#DDE2E7] px-5 py-2.5 text-sm font-semibold text-[#243447] transition hover:bg-[#E8EEF4]";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F8FA] px-6 text-[#252A31]" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl border border-[#DDE2E7] bg-white p-8 text-center shadow-lg">
        <div className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#243447] text-white"><Radio className="h-5 w-5" /></div>
          <span className="text-lg font-bold text-[#243447]">CommConnect</span>
        </div>

        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#5F7FA3]" />
            <h1 className="mt-5 text-xl font-bold text-[#243447]">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-[#565E6B]">This only takes a moment.</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#5F7FA3]" />
            <h1 className="mt-5 text-2xl font-bold text-[#243447]">You're all set! 🎉</h1>
            <p className="mt-3 text-sm text-[#565E6B]">
              Thank you for signing up for CommConnect. Our team has your details and will reach out within one business day to begin your onboarding and configuration.
            </p>
            <button onClick={() => navigate("/home")} data-testid="success-home-btn" className={`${primaryBtn} mt-6`}>Back to home</button>
          </div>
        )}

        {status === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#C2A46D]" />
            <h1 className="mt-5 text-xl font-bold text-[#243447]">Payment is processing</h1>
            <p className="mt-2 text-sm text-[#565E6B]">Your payment is still being confirmed. You'll receive an email shortly — no need to pay again.</p>
            <button onClick={() => navigate("/home")} className={`${ghostBtn} mt-6`}>Back to home</button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="payment-error">
            <XCircle className="mx-auto h-14 w-14 text-muted-foreground" />
            <h1 className="mt-5 text-xl font-bold text-[#243447]">We couldn't confirm your payment</h1>
            <p className="mt-2 text-sm text-[#565E6B]">If you were charged, don't worry — reach out to us and we'll sort it out right away.</p>
            <button onClick={() => navigate("/pricing")} data-testid="error-pricing-btn" className={`${primaryBtn} mt-6`}>Back to pricing</button>
          </div>
        )}
      </div>
    </div>
  );
}
